import React, { useCallback, useMemo } from "react";
import { IUser, IPolicyDoc, ICrewBase, ISimpleUser, PolicyResource, PolicyAction } from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikSelectField,
  FormikPhoneField,
  FormikSwitchField,
} from "../../../MUI5FormikComponents/FormikWrappers";
import { Button, Grid, Typography, ButtonGroup } from "@mui/material";
import * as Yup from "yup";
import { emailValidation, phoneNumberValidation } from "../../Validation/validation";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch, useSelector } from "react-redux";

import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IUsersFormProps {
  users: ISimpleUser[];
  handleRetrieveUser: (user: ISimpleUser) => void;
  selectedUser?: IUser | null;
  className?: string;
  updateUserList: (user: IUser, mode: "create" | "update" | "delete") => void;
  policies: IPolicyDoc[];
  crewBases: ICrewBase[];
  setActiveModal: (activeModal: IActiveModal | null) => void;
}

//!! TODO: Interface review for frontend
type IUserWithoutCrew = Omit<IUser, "crewInfo">;

const initialUserValues: IUserWithoutCrew = {
  _id: undefined,
  firstName: "",
  lastName: "",
  email: "",
  sendEmail: true,
  active: true,
  allowInternalSignIn: false,
  phoneCell: undefined,
  employeeId: undefined,
  preferredName: undefined,
  middleName: undefined,
  policies: [],
  isApiUser: false,
  token: "",
  foreignRef: [],
  sub: [],
  createdBy: "",
};
type UserForm = FormikProps<IUserWithoutCrew>;

// User Form Validation with YUP
const UserSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string()
    .matches(emailValidation, "Please enter a valid email address")
    .required("Email is required")
    .lowercase(),
  phoneCell: Yup.string()
    .test("regex", "Please enter a valid cellphone number", (number) => {
      if (!number) return true; // return value if undefined (don't run regex).
      return number.length > 0 && phoneNumberValidation.exec(number) ? true : false;
    })
    .nullable(),
  preferredName: Yup.string().notRequired().nullable(),
  middleName: Yup.string().notRequired().nullable(),
  employeeId: Yup.string().notRequired().nullable(),
});

export const resendConfirmationEmail = async (user: IUserWithoutCrew, dispatch: any): Promise<void> => {
  const { email } = user;
  if (!email) return;
  const notifications: INotifications = {
    dispatch: dispatch,
    infoMessage: `Sending invitation email to: ${email}`,
    successMessage: "Invitation email sent successfully.",
    errorMessage: "Error: Unable to send invitation or user has already accepted.",
  };
  await AmplifyAPIWrapper.Put<string[]>("/user/resendConfirmationEmails", [email], notifications);
};

export const fixPhoneNumToString = (phoneNum: number | string | null | undefined): string | undefined =>
  !phoneNum ? undefined : `${phoneNum}`;

export const fixPhoneNumStringToNum = (phoneNum: number | string | null | undefined): number | undefined => {
  const phNum = !phoneNum ? null : typeof phoneNum === "string" ? Number(phoneNum.replace(/\D/g, "")) : phoneNum;
  return phNum !== 0 ? phNum : undefined;
};

export const UsersForm = (props: IUsersFormProps): JSX.Element => {
  const { users, handleRetrieveUser, selectedUser, className, updateUserList, policies, crewBases, setActiveModal } =
    props;
  const dispatch = useDispatch();
  const initialValues = selectedUser ? selectedUser : initialUserValues;

  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);
  const operatingBases = useMemo(() => {
    return crewBases?.length > 0
      ? crewBases
          .filter((base: ICrewBase) => base.isCounter === true)
          .sort((a, b) => {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          })
      : [];
  }, [crewBases]);

  const checkForExistingUser = useCallback(
    (email: string) => {
      const isEmailValid = emailValidation.exec(email);
      if (isEmailValid) {
        const matchingUser = users.find((user) => user.email === email);
        if (matchingUser?._id) {
          const modalTitle = "Matching User Found";
          const modalMessage = "User with this email already exists, select them now?";
          const confirmAction = (): void => {
            setActiveModal(null);
            handleRetrieveUser(matchingUser);
          };
          const confirmLabel = "Select User";
          // DISPLAY MODAL
          setActiveModal({
            title: modalTitle,
            message: modalMessage,
            cancel: () => setActiveModal(null),
            confirm: confirmAction,
            confirmLabel: confirmLabel,
          });
        }
      }
    },
    [setActiveModal, users, handleRetrieveUser]
  );

  const handleSubmit = async (values: IUserWithoutCrew, helpers: FormikHelpers<IUserWithoutCrew>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const mode = values._id ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} User...`,
      successMessage: `User Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} User`,
    };
    const response =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<IUser>("/user", values, notifications)
        : await AmplifyAPIWrapper.Put<IUser>(`/user/${values._id}`, values, notifications);
    if (response?._id) {
      const updatedValues = { ...response, policies: values.policies };
      resetForm({ values: updatedValues });
      setStatus({ success: true });
      // add or update user from main list.
      updateUserList(updatedValues, mode);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      className={className}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={UserSchema}
    >
      {(userForm: UserForm) => {
        const { dirty, isValid, isSubmitting, handleReset } = userForm;
        const handleToggleSwitch = (e: string, fieldName: string): void => {
          const toggleVal = e === "true";
          userForm.setFieldValue(fieldName, toggleVal);
          userForm.setFieldTouched(fieldName, true);
        };

        return (
          <Form>
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                  Required Fields
                </Typography>
                <FormikSwitchField
                  label="User Is Active"
                  name="active"
                  value={true}
                  onChange={(e) => {
                    handleToggleSwitch(e, "active");
                  }}
                />
                <FormikSwitchField
                  label="Allow Internal Sign In"
                  name="allowInternalSignIn"
                  value={true}
                  onChange={(e) => {
                    handleToggleSwitch(e, "allowInternalSignIn");
                  }}
                />
                <FormikTextField
                  className="field textfield"
                  label="Email"
                  name="email"
                  parse={(email) => {
                    if (!selectedUser && users?.length > 0 && !!email) {
                      // If creating a new user, check if they are already crew or users once email is available.
                      checkForExistingUser(email);
                    }
                    return email.toLowerCase();
                  }}
                />
                <FormikTextField className="field textfield" label="First Name" name="firstName" />
                <FormikTextField className="field textfield" label="Last Name" name="lastName" />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                  Optional Fields
                </Typography>
                <FormikTextField className="field textfield" label="Employee ID" name="employeeId" />
                <FormikTextField className="field textfield" label="Preferred Name" name="preferredName" />
                <FormikTextField className="field textfield" label="Middle Name" name="middleName" />
                <FormikPhoneField
                  className="field textfield"
                  label="Cell/SMS#"
                  name="phoneCell"
                  format={fixPhoneNumToString}
                  parse={fixPhoneNumStringToNum}
                />
                <input type="hidden" name="operatingBase" />

                <FormikSelectField
                  name="policies"
                  label="Policies"
                  options={policies}
                  isMulti={true}
                  getOptionLabel={(policy: IPolicyDoc) => policy.policyName}
                  getOptionValue={(policy: IPolicyDoc) => policy._id}
                  isSearchable={true}
                  isClearable={true}
                  hideSelectedOptions={false}
                  disabled={!hasPermissions(PolicyResource.User, "policies", PolicyAction.PermissionsUpdate)}
                />
                {!selectedUser && (
                  <FormikSwitchField
                    label="Send invitation email?"
                    name="sendEmail"
                    onChange={(e) => {
                      handleToggleSwitch(e, "sendEmail");
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <ButtonGroup variant="contained">
                  {selectedUser && (
                    <Button
                      color="info"
                      disabled={!selectedUser?.email || isSubmitting}
                      onClick={() => resendConfirmationEmail(selectedUser, dispatch)}
                    >
                      Send Invitation Email
                    </Button>
                  )}
                  <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                    {selectedUser ? "Update" : "Create"}
                  </Button>
                  <Button
                    type="reset"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
